// lib/selectOptionsCache.ts
import { log } from '../utils/logger';
import type { OptionMap, OptionKey } from './optionTypes';
import { derivedKeys } from './optionTypes';

const cache: Partial<OptionMap> = {};
const listeners: Set<() => void> = new Set();
let isNotifying = false;

export function getCachedOption<K extends OptionKey>(key: K): OptionMap[K] | undefined {
  const value = cache[key];
  if (key === 'bulkRawMaterials') {
    log.trace('[optionsCache] getCachedOption bulkRawMaterials:', () =>
      value ? `Found ${Array.isArray(value) ? value.length : 'N/A'} items` : 'NOT FOUND'
    );
  }
  return value;
}

export function setCachedOption<K extends OptionKey>(key: K, value: OptionMap[K]) {
  if (key === 'bulkRawMaterials') {
    log.trace('[optionsCache] setCachedOption bulkRawMaterials:', () =>
      Array.isArray(value) ? `${value.length} items` : 'Invalid data'
    );
  }
  cache[key] = value;

  // Prevent recursive notifications
  if (!isNotifying) {
    isNotifying = true;
    try {
      // Notify all listeners that the cache has changed
      listeners.forEach((listener) => listener());
    } finally {
      isNotifying = false;
    }
  }
}

export function subscribeToCacheChanges(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function invalidateCachedOption<K extends OptionKey>(key: K) {
  delete cache[key];

  // Notify all listeners that the cache has changed
  if (!isNotifying) {
    isNotifying = true;
    try {
      listeners.forEach((listener) => listener());
    } finally {
      isNotifying = false;
    }
  }
}

export function invalidateCachedOptions(keys: OptionKey[]) {
  // Invalidate the keys themselves
  keys.forEach((key) => delete cache[key]);

  // Invalidate all derived keys that depend on any of the base keys
  for (const [derivedKey, baseKeys] of derivedKeys.entries()) {
    // If any of the base keys that this derived key depends on is being invalidated
    if (baseKeys.some((baseKey) => keys.includes(baseKey))) {
      delete cache[derivedKey];
    }
  }

  // Notify all listeners that the cache has changed
  if (!isNotifying) {
    isNotifying = true;
    try {
      listeners.forEach((listener) => listener());
    } finally {
      isNotifying = false;
    }
  }
}
